import { useRef, useState } from "react";
import toast, { Toaster, ToastBar, type Toast } from "react-hot-toast";
import { X } from "lucide-react";

/** Toasts that must stay up until something else resolves them — no X, no swipe. */
const PERSISTENT_TOAST_IDS = new Set(["network-status"]);

/** Horizontal drag distance, in px, that counts as "swiped away". */
const SWIPE_DISMISS_PX = 72;

function canDismiss(t: Toast): boolean {
  return t.type !== "loading" && !PERSISTENT_TOAST_IDS.has(t.id);
}

/**
 * One toast's row: react-hot-toast's own icon + message, plus the dismiss
 * affordances every toast needs except a loading toast (it resolves on its
 * own) and the persistent offline warning (it must stay up until the
 * connection returns) — a small X, and on touch devices a swipe-right that
 * tracks the finger and dismisses past `SWIPE_DISMISS_PX`.
 *
 * The drag only ever transforms this inner row; react-hot-toast's own
 * enter/exit animation lives on `ToastBar`'s own wrapper, so the two never
 * fight over the same transform.
 */
function DismissibleToastBar({ toast: t }: { toast: Toast }) {
  const dismissible = canDismiss(t);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startXRef = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!dismissible) return;
    startXRef.current = e.touches[0].clientX;
    setDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dismissible || !dragging) return;
    // Rightward only — a swipe left has no meaning here and would otherwise
    // rubber-band the toast off its own left edge.
    setDragX(Math.max(0, e.touches[0].clientX - startXRef.current));
  };

  const handleTouchEnd = () => {
    if (!dismissible) return;
    setDragging(false);
    if (dragX > SWIPE_DISMISS_PX) {
      toast.dismiss(t.id);
    } else {
      setDragX(0);
    }
  };

  return (
    <ToastBar toast={t}>
      {({ icon, message }) => (
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="flex items-center gap-2 w-full"
          style={{
            // Claim horizontal drags ourselves; let vertical scrolls (and the
            // page's own gestures) pass through untouched.
            touchAction: "pan-y",
            transform: dragX ? `translateX(${dragX}px)` : undefined,
            opacity: dragX
              ? Math.max(0.2, 1 - dragX / (SWIPE_DISMISS_PX * 2))
              : 1,
            transition: dragging
              ? "none"
              : "transform 200ms ease, opacity 200ms ease",
          }}
        >
          {icon}
          <div className="flex-1">{message}</div>
          {dismissible && (
            <button
              type="button"
              onClick={() => toast.dismiss(t.id)}
              aria-label="Dismiss notification"
              className="shrink-0 text-warm-text-tertiary hover:text-warm-text-secondary transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}
    </ToastBar>
  );
}

/**
 * The app's single toast container. All styling/positioning lives here so
 * every toast — however it's raised, across the whole app — gets the same
 * dismiss affordances for free, with no per-call-site changes.
 */
export default function NotificationToaster() {
  return (
    <Toaster
      position="top-right"
      gutter={12}
      containerStyle={{ top: 20, right: 20 }}
      toastOptions={{
        duration: 4000,
        className: "toast-notification",
        style: {
          background: "var(--color-bg-elevated)",
          color: "var(--color-text-primary)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "12px",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          fontFamily:
            '"DM Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
          fontSize: "0.875rem",
          fontWeight: 500,
          padding: "14px 18px",
          maxWidth: "380px",
          lineHeight: "1.4",
        },
        success: {
          duration: 3000,
          iconTheme: {
            primary: "var(--color-primary)",
            secondary: "#fff",
          },
        },
        error: {
          iconTheme: {
            primary: "var(--color-recording)",
            secondary: "#fff",
          },
        },
      }}
    >
      {(t) => <DismissibleToastBar toast={t} />}
    </Toaster>
  );
}
